/**
 * Structure Panel component - displays questionnaire structure as a hierarchical tree
 */
import React, { useState, useEffect, useMemo, useCallback } from "react"
import { Tree, Input } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import type { DataNode } from "antd/es/tree"
import type { EventDataNode } from "antd/es/tree"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSitemap, faSync, faTimes, faSearch, faPlus } from "@fortawesome/free-solid-svg-icons"
import { Loader } from "shared/ui/Loader/Loader.component"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { Editor as TiptapEditor } from "@tiptap/react"
import { EditorRef } from "types/questionnaire.types"
import { StructureNode } from "utils/structureParser"
import { getNodeLabel, findNodeByKey } from "utils/structureTreeHelpers"
import { htmlToText, textToHtml } from "utils/htmlTextConverter"
import {
  useStructurePanel,
  useTreeFilter,
  useDropIndicator,
  useTemplateDrop,
} from "features/QuestionnaireEditor/hooks/Structure"
import AddElementModal from "./AddElementModal.component"

interface DropInfo {
  node: EventDataNode<DataNode>
  dragNode: EventDataNode<DataNode>
  dropPosition: number
  dropToGap: boolean
}

interface StructurePanelProps {
  editorRef: React.RefObject<EditorRef | null>
  isVisible: boolean
  scrollToLine?: (lineNumber: number) => void
  editor: TiptapEditor | null
  onClose?: () => void
  addElementModalVisible: boolean
  setAddElementModalVisible: (visible: boolean) => void
  /** Line number to jump to and select in the tree */
  jumpToLineNumber?: number
  /** Callback when jump is complete */
  onJumpComplete?: () => void
}

/**
 * Convert structure node to Ant Design Tree data format
 */
const convertToAntdTree = (
  node: StructureNode,
  onNodeClick: (node: StructureNode) => void,
  onExternalDrop: (e: React.DragEvent, nodeKey: React.Key, position: "before" | "after" | "inside") => void,
  onExternalDragOver: (nodeKey: React.Key, position: "before" | "after" | "inside") => void,
  dragOverKey: React.Key | null,
  dropPos: { nodeKey: React.Key; position: "before" | "after" | "inside" } | null
): DataNode[] => {
  const showDropIndicator = dropPos?.nodeKey === node.id
  const dropPosition = dropPos?.position

  const calculateDropPosition = (e: React.DragEvent<HTMLElement>, nodeKey: string): "before" | "after" | "inside" => {
    // Find the actual card element (content wrapper) for this node
    const treeNodes = document.querySelectorAll('.ant-tree-treenode')
    let cardElement: HTMLElement | null = null

    for (const treeNode of treeNodes) {
      const wrapper = treeNode.querySelector('.structure-node-wrapper') as HTMLElement
      if (wrapper?.getAttribute('data-node-key') === nodeKey) {
        cardElement = treeNode.querySelector('.ant-tree-node-content-wrapper') as HTMLElement
        break
      }
    }

    if (!cardElement) {
      // Fallback to old behavior if card not found
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
      const mouseY = e.clientY - rect.top
      const height = rect.height

      if (mouseY < height * 0.4) {
        return "before"
      } else if (mouseY > height * 0.6) {
        return "after"
      } else {
        return "inside"
      }
    }

    const rect = cardElement.getBoundingClientRect()
    const mouseY = e.clientY - rect.top
    const height = rect.height

    // More generous zones: 40% top = before, 40% bottom = after, 20% middle = inside
    if (mouseY < height * 0.4) {
      return "before"
    } else if (mouseY > height * 0.6) {
      return "after"
    } else {
      return "inside"
    }
  }

  const title = (
    <div
      className={`structure-node-wrapper ${showDropIndicator && dropPosition === "before" ? "drop-target-before" : ""} ${showDropIndicator && dropPosition === "after" ? "drop-target-after" : ""}`}
      data-node-key={node.id}
      onDragOver={(e) => {
        // Only handle external template drags
        if (e.dataTransfer.types.includes("application/template")) {
          e.preventDefault()
          e.dataTransfer.dropEffect = "copy"

          // Calculate and update drop position indicator based on actual card bounds
          const position = calculateDropPosition(e, node.id)
          onExternalDragOver(node.id, position)
        }
      }}
      onDrop={(e) => {
        // Handle drop on the node itself using the current drop position
        if (e.dataTransfer.types.includes("application/template") && showDropIndicator && dropPosition) {
          e.preventDefault()
          e.stopPropagation()

          const templateKey = e.dataTransfer.getData("application/template")
          if (templateKey) {
            const syntheticEvent = {
              ...e,
              dataTransfer: e.dataTransfer,
              preventDefault: () => {},
              stopPropagation: () => {}
            } as React.DragEvent

            onExternalDrop(syntheticEvent, node.id, dropPosition)
          }
        }
      }}
    >
      {showDropIndicator && dropPosition === "before" && (
        <div className="drop-indicator drop-indicator-before"></div>
      )}
      <span
        className={`structure-node-title ${showDropIndicator && dropPosition === "inside" ? "drop-indicator-inside" : ""}`}
        title={node.lineNumber > 0 ? `Line ${node.lineNumber}` : undefined}
      >
        {getNodeLabel(node)}
      </span>
      {showDropIndicator && dropPosition === "after" && (
        <div className="drop-indicator drop-indicator-after"></div>
      )}
    </div>
  )

  const result: DataNode = {
    key: node.id,
    title,
    children: node.children?.map((child) => convertToAntdTree(child, onNodeClick, onExternalDrop, onExternalDragOver, dragOverKey, dropPos)[0]),
  }

  return [result]
}

/**
 * StructurePanel Component
 */
export const StructurePanel: React.FC<StructurePanelProps> = ({
  editorRef,
  isVisible,
  scrollToLine,
  editor,
  onClose,
  addElementModalVisible,
  setAddElementModalVisible,
  jumpToLineNumber,
  onJumpComplete,
}) => {
  // Custom hooks
  const { structureTree, parseStructure, isLoading } = useStructurePanel(editorRef)
  const { searchTerm, setSearchTerm, filteredTree } = useTreeFilter(structureTree)
  const { dropPosition, updateDropPosition, clearDropPosition } = useDropIndicator()
  const { handleTemplateDrop } = useTemplateDrop({
    editor,
    editorRef,
    structureTree,
    parseStructure,
    scrollToLine,
  })

  // Local state
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([])
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([])
  const [dragOverNodeKey, setDragOverNodeKey] = useState<React.Key | null>(null)

  // Parse structure when panel becomes visible
  useEffect(() => {
    if (isVisible && editorRef.current) {
      parseStructure()
    }
  }, [isVisible, parseStructure, editorRef])

  // Helper function to find node by line number and collect all parent keys
  const findNodeAndParentsByLine = useCallback(
    (tree: StructureNode | null, lineNumber: number, parents: React.Key[] = []): { node: StructureNode; parents: React.Key[] } | null => {
      if (!tree) return null

      // Check if this node contains the line number
      if (tree.lineNumber <= lineNumber && (tree.endLineNumber ? tree.endLineNumber >= lineNumber : tree.lineNumber === lineNumber)) {
        // If node has children, check them first (more specific match)
        if (tree.children) {
          for (const child of tree.children) {
            const result = findNodeAndParentsByLine(child, lineNumber, [...parents, tree.id])
            if (result) return result
          }
        }
        // This is the most specific node containing the line
        return { node: tree, parents }
      }

      // Check children
      if (tree.children) {
        for (const child of tree.children) {
          const result = findNodeAndParentsByLine(child, lineNumber, [...parents, tree.id])
          if (result) return result
        }
      }

      return null
    },
    []
  )

  // Handle jump to line number
  useEffect(() => {
    if (jumpToLineNumber && structureTree && isVisible && !isLoading) {
      const result = findNodeAndParentsByLine(structureTree, jumpToLineNumber)
      if (result) {
        const { node, parents } = result

        // Expand all parent nodes
        setExpandedKeys(prevKeys => {
          const newKeys = new Set([...prevKeys, ...parents])
          return Array.from(newKeys)
        })

        // Select the target node
        setSelectedKeys([node.id])

        // Scroll to the node in the tree after a short delay to ensure it's rendered
        setTimeout(() => {
          const treeNodes = document.querySelectorAll('.ant-tree-treenode')
          for (const treeNode of treeNodes) {
            const wrapper = treeNode.querySelector('.structure-node-wrapper') as HTMLElement
            if (wrapper?.getAttribute('data-node-key') === node.id) {
              wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' })
              break
            }
          }
        }, 100)

        // Call completion callback
        onJumpComplete?.()
      }
    }
  }, [jumpToLineNumber, structureTree, isVisible, isLoading, findNodeAndParentsByLine, onJumpComplete])

  // Handle node click - jump to line
  const handleNodeClick = useCallback(
    (node: StructureNode) => {
      if (node.lineNumber > 0 && scrollToLine) {
        scrollToLine(node.lineNumber)
      }
    },
    [scrollToLine]
  )

  // Handle tree node selection
  const handleSelect = useCallback(
    (selectedKeys: React.Key[]) => {
      if (selectedKeys.length === 0 || !structureTree) return

      const selectedNode = findNodeByKey(structureTree, String(selectedKeys[0]))
      if (selectedNode) {
        handleNodeClick(selectedNode)
      }
    },
    [structureTree, handleNodeClick]
  )

  // Handle external drag over - update drop position indicator
  const handleExternalDragOver = useCallback(
    (nodeKey: React.Key, position: "before" | "after" | "inside") => {
      updateDropPosition(nodeKey, position)
    },
    [updateDropPosition]
  )

  // Handle external template drop from modal
  const handleExternalDrop = useCallback(
    (e: React.DragEvent, targetNodeKey: React.Key, position: "before" | "after" | "inside") => {
      handleTemplateDrop(e, targetNodeKey, position)
      setDragOverNodeKey(null)
      clearDropPosition()
    },
    [handleTemplateDrop, clearDropPosition]
  )

  // Convert to Ant Design tree data
  const treeData = useMemo(() => {
    if (!filteredTree) return []
    return convertToAntdTree(filteredTree, handleNodeClick, handleExternalDrop, handleExternalDragOver, dragOverNodeKey, dropPosition)
  }, [filteredTree, handleNodeClick, handleExternalDrop, handleExternalDragOver, dragOverNodeKey, dropPosition])

  // Handle drag and drop
  const handleDrop = useCallback(
    (info: DropInfo) => {
      const dropKey = info.node.key
      const dragKey = info.dragNode?.key
      const dropPos = info.node.pos.split("-")
      const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1])

      if (!editorRef.current || !structureTree) {
        return
      }

      // Check if this is an external drop (from template modal)
      if (!dragKey) {
        // External drop - will be handled by node-level handlers
        return
      }

      const draggedNode = findNodeByKey(structureTree, String(dragKey))
      const dropNode = findNodeByKey(structureTree, String(dropKey))

      if (!draggedNode || !draggedNode.endLineNumber) {
        notify.toast.error("Cannot move this element - boundary not detected")
        return
      }

      // Get current editor content as HTML
      const editorHTML = editorRef.current.getHTML()
      const plainText = htmlToText(editorHTML)
      const lines = plainText.split("\n")

      // Extract the dragged element content (lines from lineNumber to endLineNumber)
      const draggedLines = lines.slice(draggedNode.lineNumber - 1, draggedNode.endLineNumber)
      const draggedContent = draggedLines.join("\n")

      // Remove dragged content from original position
      const remainingLines = [
        ...lines.slice(0, draggedNode.lineNumber - 1),
        ...lines.slice(draggedNode.endLineNumber),
      ]

      // Calculate insertion position
      let insertIndex: number
      if (dropNode && dropNode.lineNumber > 0) {
        // Adjust for removed lines if drop is after drag
        const adjustment = dropNode.lineNumber > draggedNode.lineNumber ? draggedLines.length : 0

        if (dropPosition === -1) {
          // Drop before the target
          insertIndex = dropNode.lineNumber - 1 - adjustment
        } else if (dropPosition === 1) {
          // Drop after the target
          const targetEndLine = dropNode.endLineNumber || dropNode.lineNumber
          insertIndex = targetEndLine - adjustment
        } else {
          // Drop as child (inside the target)
          insertIndex = dropNode.lineNumber - adjustment
        }
      } else {
        notify.toast.error("Invalid drop target")
        return
      }

      // Insert dragged content at new position
      const newLines = [
        ...remainingLines.slice(0, insertIndex),
        draggedContent,
        ...remainingLines.slice(insertIndex),
      ]

      // Update editor content
      const newContent = newLines.join("\n")
      const newHTML = textToHtml(newContent)
      editorRef.current.setHTML(newHTML)

      // Reparse structure
      setTimeout(() => {
        parseStructure()
      }, 100)

      notify.toast.success("Element moved successfully")
    },
    [editorRef, structureTree, parseStructure]
  )

  return (
    <div className={`structure-panel ${isVisible ? "visible" : "hidden"}`}>
      {/* Header */}
      <div className="structure-header">
        <div className="structure-header-left">
          <div className="structure-icon">
            <FontAwesomeIcon icon={faSitemap} />
          </div>
          <h3>Structure</h3>
        </div>
        <div className="structure-header-right">
          <button
            onClick={parseStructure}
            className="structure-refresh-btn"
            title="Refresh structure"
          >
            <FontAwesomeIcon icon={faSync} />
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="structure-close-btn"
              title="Close panel (Ctrl+Shift+E)"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
      </div>

      {/* Search and Add Button */}
      <div className="structure-search">
        <Input
          placeholder="Search structure..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          prefix={<FontAwesomeIcon icon={faSearch} style={{ color: '#36C3ED' }} />}
          allowClear
          size="small"
        />
        <DataQButton
          type="primary"
          onClick={() => setAddElementModalVisible(true)}
          className="mt-2 w-full"
        >
          <FontAwesomeIcon icon={faPlus} /> Add Survey Element
        </DataQButton>
      </div>

      {/* Tree */}
      <div
        className="structure-tree"
        onDragOver={(e) => {
          // Allow external drops
          if (e.dataTransfer.types.includes("application/template")) {
            e.preventDefault()
            e.dataTransfer.dropEffect = "copy"

            // Find closest node to mouse position using the actual card elements
            const mouseY = e.clientY
            const treeNodes = document.querySelectorAll('.ant-tree-treenode')
            let closestNode: { element: HTMLElement; distance: number; nodeKey: string } | null = null as { element: HTMLElement; distance: number; nodeKey: string } | null

            treeNodes.forEach((treeNode) => {
              // Get the content wrapper (the actual card)
              const contentWrapper = treeNode.querySelector('.ant-tree-node-content-wrapper')
              if (!contentWrapper) return

              const rect = contentWrapper.getBoundingClientRect()
              const nodeCenter = rect.top + rect.height / 2
              const distance = Math.abs(mouseY - nodeCenter)

              if (!closestNode || distance < closestNode.distance) {
                // Get node key from the wrapper inside
                const wrapper = contentWrapper.querySelector('.structure-node-wrapper') as HTMLElement
                const nodeKey = wrapper?.getAttribute('data-node-key')
                if (nodeKey) {
                  closestNode = {
                    element: contentWrapper as HTMLElement,
                    distance,
                    nodeKey
                  }
                }
              }
            })

            // Update drop position based on closest node
            if (closestNode) {
              const rect = closestNode.element.getBoundingClientRect()
              const mouseYRelative = mouseY - rect.top
              const height = rect.height

              let position: "before" | "after" | "inside"
              // More generous zones: 40% top = before, 40% bottom = after, 20% middle = inside
              if (mouseYRelative < height * 0.4) {
                position = "before"
              } else if (mouseYRelative > height * 0.6) {
                position = "after"
              } else {
                position = "inside"
              }

              updateDropPosition(closestNode.nodeKey, position)
            }
          }
        }}
        onDragLeave={(e) => {
          // Clear drop position when leaving tree area
          if (e.dataTransfer.types.includes("application/template")) {
            // Check if we're actually leaving the container boundary
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
            const isOutside =
              e.clientX < rect.left ||
              e.clientX > rect.right ||
              e.clientY < rect.top ||
              e.clientY > rect.bottom

            if (isOutside) {
              clearDropPosition()
            }
          }
        }}
        onDrop={(e) => {
          // Handle drops on empty space between nodes
          if (e.dataTransfer.types.includes("application/template")) {
            e.preventDefault()
            e.stopPropagation()

            const templateKey = e.dataTransfer.getData("application/template")
            if (templateKey) {
              // If dropPosition exists, use it. Otherwise, calculate it now
              let targetNodeKey: React.Key
              let targetPosition: "before" | "after" | "inside"

              if (dropPosition) {
                targetNodeKey = dropPosition.nodeKey
                targetPosition = dropPosition.position
              } else {
                // Fallback: calculate position at drop time using the actual card elements
                const mouseY = e.clientY
                const treeNodes = document.querySelectorAll('.ant-tree-treenode')
                let closestNode = null as { element: HTMLElement; distance: number; nodeKey: string } | null

                treeNodes.forEach((treeNode) => {
                  // Get the content wrapper (the actual card)
                  const contentWrapper = treeNode.querySelector('.ant-tree-node-content-wrapper')
                  if (!contentWrapper) return

                  const rect = contentWrapper.getBoundingClientRect()
                  const nodeCenter = rect.top + rect.height / 2
                  const distance = Math.abs(mouseY - nodeCenter)

                  if (!closestNode || distance < closestNode.distance) {
                    // Get node key from the wrapper inside
                    const wrapper = contentWrapper.querySelector('.structure-node-wrapper') as HTMLElement
                    const nodeKey = wrapper?.getAttribute('data-node-key')
                    if (nodeKey) {
                      closestNode = { element: contentWrapper as HTMLElement, distance, nodeKey }
                    }
                  }
                })

                if (!closestNode) return

                const rect = closestNode.element.getBoundingClientRect()
                const mouseYRelative = mouseY - rect.top
                const height = rect.height

                targetNodeKey = closestNode.nodeKey
                if (mouseYRelative < height * 0.4) {
                  targetPosition = "before"
                } else if (mouseYRelative > height * 0.6) {
                  targetPosition = "after"
                } else {
                  targetPosition = "inside"
                }
              }

              // Create a synthetic event to pass to handleExternalDrop
              const syntheticEvent = {
                ...e,
                dataTransfer: e.dataTransfer,
                preventDefault: () => {},
                stopPropagation: () => {}
              } as React.DragEvent

              handleExternalDrop(syntheticEvent, targetNodeKey, targetPosition)
            }

            // Clear drop position after drop
            clearDropPosition()
          }
        }}
      >
        {isLoading ? (
          <div className="flex items-center justify-center p-4">
            <Loader fontSize={24} color="#36C3ED" />
          </div>
        ) : treeData.length > 0 ? (
          <Tree
            treeData={treeData}
            showIcon={false}
            showLine={true}
            expandedKeys={expandedKeys}
            onExpand={(keys) => setExpandedKeys(keys)}
            selectedKeys={selectedKeys}
            onSelect={(keys) => {
              setSelectedKeys(keys)
              handleSelect(keys)
            }}
            defaultExpandAll={false}
            blockNode
            draggable
            onDrop={handleDrop}
          />
        ) : (
          <div className="text-center text-gray-500 p-4 text-sm">
            {searchTerm ? "No results found" : "No structure available"}
          </div>
        )}
      </div>

      {/* Add Element Modal */}
      <AddElementModal
        isVisible={addElementModalVisible}
        onClose={() => setAddElementModalVisible(false)}
        editor={editor}
      />
    </div>
  )
}

export default StructurePanel
